'use client';
import React, { useState } from 'react';

const partnerCategories = ['All', 'Unsecured Loans', 'Credit Card', 'Home Loan', 'Insurance', 'Investment'];

// Using publicly available bank logo URLs from their official CDNs / Wikipedia
const partners = [
  {
    name: 'HDFC Bank',
    category: ['Unsecured Loans', 'Credit Card', 'Home Loan'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/28/HDFC_Bank_Logo.svg/320px-HDFC_Bank_Logo.svg.png',
    abbr: 'HDFC',
    color: '#004C8F',
  },
  {
    name: 'ICICI Bank',
    category: ['Unsecured Loans', 'Credit Card', 'Home Loan'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/12/ICICI_Bank_Logo.svg/320px-ICICI_Bank_Logo.svg.png',
    abbr: 'ICICI',
    color: '#F58220',
  },
  {
    name: 'SBI',
    category: ['Unsecured Loans', 'Home Loan'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/cc/SBI-logo.svg/240px-SBI-logo.svg.png',
    abbr: 'SBI',
    color: '#22409A',
  },
  {
    name: 'Axis Bank',
    category: ['Unsecured Loans', 'Credit Card'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/Axis_Bank_logo.svg/320px-Axis_Bank_logo.svg.png',
    abbr: 'AXIS',
    color: '#97144D',
  },
  {
    name: 'Kotak Bank',
    category: ['Unsecured Loans', 'Credit Card'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/Kotak_Mahindra_Bank_logo.svg/320px-Kotak_Mahindra_Bank_logo.svg.png',
    abbr: 'KMB',
    color: '#EF4123',
  },
  {
    name: 'Bajaj Finance',
    category: ['Unsecured Loans'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8b/Bajaj_Finance_logo.svg/320px-Bajaj_Finance_logo.svg.png',
    abbr: 'BFL',
    color: '#0066B3',
  },
  {
    name: 'LIC Housing',
    category: ['Home Loan'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7e/LIC_Housing_Finance_logo.png/240px-LIC_Housing_Finance_logo.png',
    abbr: 'LIC',
    color: '#1A5276',
  },
  {
    name: 'Tata Capital',
    category: ['Unsecured Loans', 'Home Loan'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Tata_Capital_Logo.png/320px-Tata_Capital_Logo.png',
    abbr: 'TATA',
    color: '#003087',
  },
  {
    name: 'IDFC First',
    category: ['Unsecured Loans', 'Credit Card'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/IDFC_FIRST_Bank_Logo.svg/320px-IDFC_FIRST_Bank_Logo.svg.png',
    abbr: 'IDFC',
    color: '#E8272A',
  },
  {
    name: 'Yes Bank',
    category: ['Unsecured Loans', 'Credit Card'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Yes_Bank_Logo.svg/320px-Yes_Bank_Logo.svg.png',
    abbr: 'YES',
    color: '#0033A0',
  },
  {
    name: 'LIC',
    category: ['Insurance'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7e/LIC_logo.svg/240px-LIC_logo.svg.png',
    abbr: 'LIC',
    color: '#1A5276',
  },
  {
    name: 'HDFC Life',
    category: ['Insurance'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/28/HDFC_Bank_Logo.svg/320px-HDFC_Bank_Logo.svg.png',
    abbr: 'HDFC',
    color: '#004C8F',
  },
  {
    name: 'Mutual Funds',
    category: ['Investment'],
    logo: '',
    abbr: 'MF',
    color: '#E07B1A',
  },
  {
    name: 'NPS Trust',
    category: ['Investment'],
    logo: '',
    abbr: 'NPS',
    color: '#2E86AB',
  },
  {
    name: 'PNB Housing',
    category: ['Home Loan'],
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e0/Punjab_National_Bank_logo.svg/240px-Punjab_National_Bank_logo.svg.png',
    abbr: 'PNB',
    color: '#1B4F72',
  },
  {
    name: 'Fullerton',
    category: ['Unsecured Loans'],
    logo: '',
    abbr: 'FULL',
    color: '#C0392B',
  },
];

export default function PartnersSection() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  const filteredPartners = activeCategory === 'All'
    ? partners
    : partners?.filter(p => p?.category?.includes(activeCategory));

  return (
    <section className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Our partners from{' '}
          <span className="block sm:inline text-primary">across the industry</span>
        </h2>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {partnerCategories?.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                activeCategory === cat
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-white text-gray-600 border border-gray-300 hover:border-primary hover:text-primary'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Partner logos grid */}
        <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-4">
          {filteredPartners?.map((partner) => (
            <div
              key={partner?.name}
              className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col items-center justify-center gap-1 hover:border-orange-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
              style={{ minHeight: '80px' }}
            >
              {partner.logo && !imgErrors[partner.name] ? (
                <img
                  src={partner.logo}
                  alt={`${partner.name} logo`}
                  className="h-8 w-auto object-contain group-hover:scale-110 transition-transform duration-200"
                  onError={() => setImgErrors(prev => ({ ...prev, [partner.name]: true }))}
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold group-hover:scale-110 transition-transform duration-200"
                  style={{ backgroundColor: partner?.color }}
                >
                  {partner?.abbr}
                </div>
              )}
              <span className="text-xs text-gray-500 text-center leading-tight" style={{ fontSize: '10px' }}>{partner?.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
